import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Minus, Plus, ShieldCheck, Leaf, ArrowLeft, Play } from "lucide-react";
import { toast } from "sonner";
import { fetchProduct } from "@/lib/api";
import { useCart, formatUSD } from "@/context/CartContext";
import SynergySection from "@/components/SynergySection";

export default function ProductDetail() {
    const { slug } = useParams();
    const [product, setProduct] = useState(null);
    const [active, setActive] = useState(0);
    const [qty, setQty] = useState(1);
    const { addItem } = useCart();

    useEffect(() => {
        setActive(0);
        setQty(1);
        fetchProduct(slug)
            .then(setProduct)
            .catch(() => setProduct(null));
    }, [slug]);

    if (!product) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center text-brown">
                <p className="text-sm tracking-[0.3em] uppercase">
                    Loading bar…
                </p>
            </div>
        );
    }

    const onAdd = () => {
        addItem(product, qty);
        toast.success(`${product.name} added to your basket`);
    };

    return (
        <div
            className="bg-cream min-h-screen"
            data-testid={`product-detail-${product.slug}`}
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10 pt-8">
                <Link
                    to="/shop"
                    className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.3em] uppercase text-brown hover:text-gold transition-colors"
                    data-testid="back-to-shop-link"
                >
                    <ArrowLeft size={14} /> Back to Collection
                </Link>
            </div>

            <section className="max-w-7xl mx-auto px-5 md:px-10 pt-8 pb-16 grid md:grid-cols-12 gap-10 md:gap-14">
                {/* Gallery */}
                <div className="md:col-span-7">
                    <div className="relative aspect-[4/5] overflow-hidden bg-cream-warm rounded-sm">
                        <img
                            src={product.gallery?.[active] || product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                        />
                        <span
                            className="absolute top-5 left-5 text-[0.6rem] tracking-[0.35em] uppercase px-3 py-1.5 rounded-full bg-cream/90"
                            style={{ color: product.accent }}
                        >
                            {product.visual}
                        </span>
                        {/* Calibrated bar swatch */}
                        <div
                            className={`absolute bottom-6 right-6 w-40 h-28 ${
                                product.pattern === "flagship"
                                    ? "gold-gradient soap-bar soap-flagship"
                                    : product.pattern === "sunset"
                                      ? "sunset-gradient soap-bar"
                                      : "crystal-gradient soap-bar soap-crystal"
                            } ring-1 ring-white/60`}
                            aria-hidden
                            data-testid="product-detail-swatch"
                        />
                    </div>
                    <div className="mt-4 grid grid-cols-4 gap-3">
                        {(product.gallery || [product.image])
                            .slice(0, 4)
                            .map((g, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => setActive(i)}
                                    className={`relative aspect-square overflow-hidden rounded-sm border-2 transition-colors ${
                                        i === active
                                            ? "border-[color:var(--ig-gold)]"
                                            : "border-transparent opacity-75 hover:opacity-100"
                                    }`}
                                    data-testid={`product-thumb-${i}`}
                                >
                                    <img
                                        src={g}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        {/* ASMR video tile */}
                        <div
                            className="relative aspect-square overflow-hidden rounded-sm bg-forest flex items-center justify-center"
                            data-testid="product-asmr-tile"
                        >
                            <img
                                src={product.video_poster || product.image}
                                alt=""
                                className="absolute inset-0 w-full h-full object-cover opacity-60"
                            />
                            <div className="relative flex flex-col items-center text-cream">
                                <Play size={18} fill="currentColor" />
                                <span className="text-[0.55rem] tracking-[0.3em] uppercase mt-1">
                                    ASMR
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Details */}
                <div className="md:col-span-5 md:sticky md:top-28 self-start">
                    <span className="divider-rule text-brown">
                        {product.collection}
                    </span>
                    <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-[0.98] text-forest mt-5">
                        {product.name}
                    </h1>
                    <p className="mt-3 text-brown tracking-wide">
                        {product.tagline}
                    </p>

                    <div className="mt-6 flex items-baseline gap-3">
                        <p
                            className="font-serif text-3xl text-forest"
                            data-testid="product-price"
                        >
                            {formatUSD(product.price)}
                        </p>
                        {product.weight_oz && (
                            <span className="text-[0.65rem] tracking-[0.3em] uppercase text-brown/70">
                                Net wt. {product.weight_oz} oz
                            </span>
                        )}
                        {!product.weight_oz && product.collection && (
                            <span className="text-[0.65rem] tracking-[0.3em] uppercase text-brown/70">
                                {product.collection}
                            </span>
                        )}
                    </div>

                    <p className="mt-8 text-[0.95rem] leading-relaxed text-forest/90">
                        {product.description}
                    </p>

                    {/* Affiliate provision CTA */}
                    <div className="mt-10 flex items-center gap-4">
                        {product.affiliate_url ? (
                            <a
                                href={product.affiliate_url}
                                target="_blank"
                                rel="sponsored noopener noreferrer"
                                className="btn-pill btn-primary flex-1 justify-center"
                                data-testid="pd-affiliate-buy"
                            >
                                Shop via Partner ·{" "}
                                {formatUSD(product.price)}
                            </a>
                        ) : (
                            <button
                                onClick={onAdd}
                                className="btn-pill btn-primary flex-1 justify-center"
                                data-testid="pd-add-to-cart"
                            >
                                Add to Basket ·{" "}
                                {formatUSD(product.price * qty)}
                            </button>
                        )}
                    </div>

                    {/* Trust row */}
                    <div className="mt-8 grid grid-cols-2 gap-4 text-xs text-brown">
                        <div className="flex items-center gap-2">
                            <Leaf size={14} className="text-gold" />
                            Handmade in Hawaiʻi
                        </div>
                        <div className="flex items-center gap-2">
                            <ShieldCheck size={14} className="text-gold" />
                            Affiliate Disclosed
                        </div>
                    </div>

                    {/* Ingredients */}
                    <div
                        className="mt-10 border-t border-forest/10 pt-8"
                        data-testid="product-ingredients"
                    >
                        <h3 className="text-[0.68rem] tracking-[0.35em] uppercase text-gold mb-4">
                            Specs
                        </h3>
                        <ul className="grid grid-cols-2 gap-y-2 gap-x-6 text-sm text-forest">
                            {product.ingredients.map((i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-2"
                                >
                                    <span className="text-gold mt-1">✦</span>
                                    <span>{i}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Label notes */}
                    <div className="mt-8 p-5 bg-cream-warm rounded-sm">
                        <h4 className="text-[0.65rem] tracking-[0.3em] uppercase text-brown mb-2">
                            On the Label
                        </h4>
                        <ul className="text-sm text-forest space-y-1 font-serif italic">
                            {product.notes.map((n) => (
                                <li key={n}>— {n}</li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <SynergySection slug={product.slug} />
        </div>
    );
}
